import React from 'react'
import { useClickOutside } from '@mantine/hooks'
import { Box, Button, CopyButton, Group, Paper, Stack, Text, TextInput, Title } from '@mantine/core'
import { IconDownload } from '@tabler/icons-react'
import useTranslation from 'next-translate/useTranslation'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { useAbac } from 'react-abac'
import { getXFormUrl } from 'utils/getXFormUrl'
import { message } from 'components/ui'
import useCampaignFormStatusSwitch from '../hooks/useCampaignFormStatusSwitch'
import useCurrentUserCache from 'components/business/hooks/useCurrentUserCache'
import useApplicationRoleCache from 'components/business/hooks/useApplicationRoleCache'
import useCampaignBaseInfoCache from 'components/business/hooks/useCampaignBaseInfoCache'
import CampaignFormStatusSwitch from 'components/business/Campaign/CampaignFormStatusSwitch'

interface Props {
  id: string
  close?: () => void
}

const sanitizeFileName = (name: string) => name.replace(/[\\/:*?"<>|]/g, '_').slice(0, 80) || '问卷'

const downloadQrCodeAsPng = (url: string, fileName: string) => {
  const container = document.createElement('div')
  const root = createRoot(container)
  flushSync(() => {
    root.render(<QRCodeCanvas value={url} size={1024} bgColor="#FFFFFF" fgColor="#000000" level="M" includeMargin />)
  })
  const canvas = container.querySelector('canvas')
  if (!canvas) {
    root.unmount()
    return
  }
  canvas.toBlob((blob) => {
    if (blob) {
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = fileName
      link.href = objectUrl
      link.click()
      URL.revokeObjectURL(objectUrl)
    }
    root.unmount()
  }, 'image/png')
}

const Content = ({ id, close }: Props) => {
  const { t } = useTranslation('componentCampaignList')
  const { user } = useCurrentUserCache()
  const { userHasPermissions } = useAbac()
  const { applicationRole } = useApplicationRoleCache()
  const ref = useClickOutside(() => close?.())
  const { toggle } = useCampaignFormStatusSwitch(id)
  const { campaign } = useCampaignBaseInfoCache(id)
  if (!campaign) {
    return null
  }

  const url = getXFormUrl(campaign.token, campaign.type, true)
  const handleOpen = () => {
    window.open(url, '_blank')
  }

  const handleCopied = () => {
    message.successMsg(t('common:message.linkCopySuccess'))
  }

  const handleDownload = () => {
    downloadQrCodeAsPng(url, `${sanitizeFileName(campaign.name)}-二维码.png`)
  }

  return (
    <Stack ref={ref}>
      <Group position="apart">
        <Title order={4}>{t('deliveryUrl.title')}</Title>
        <CampaignFormStatusSwitch id={campaign.id} />
      </Group>
      <Text>{t('deliveryUrl.desc')}</Text>
      <Group>
        <Box
          onClick={handleDownload}
          sx={{
            position: 'relative',
            cursor: 'pointer',
            '& .qr-download-overlay': {
              opacity: 0,
              transition: 'opacity 150ms',
            },
            '&:hover .qr-download-overlay': {
              opacity: 1,
            },
          }}
        >
          <Paper p="xs" withBorder>
            <QRCodeSVG value={url} size={100} display="block" />
          </Paper>
          <Stack
            className="qr-download-overlay"
            spacing={4}
            align="center"
            justify="center"
            sx={(theme) => ({
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: theme.radius.sm,
              color: theme.white,
            })}
          >
            <IconDownload size={20} stroke={1.5} />
            <Text size="xs" weight={500} color="white">
              {t('deliveryUrl.download')}
            </Text>
          </Stack>
        </Box>
        <Stack className="grow">
          <TextInput value={url} readOnly />
          <Group>
            <CopyButton value={url}>
              {({ copy }) => (
                <Button
                  onClick={() => {
                    copy()
                    handleCopied()
                  }}
                >
                  {t('deliveryUrl.copy')}
                </Button>
              )}
            </CopyButton>

            <Button variant="default" onClick={handleOpen}>
              {t('deliveryUrl.open')}
            </Button>
          </Group>
        </Stack>
      </Group>
    </Stack>
  )
}

export default Content
